import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { PlaceService } from '@app/modules/model/svcplace';
import { PaymentTerminal } from '@app/modules/model/payment-terminal';
import { PAYTERMINAL_PAYTEF, PAYTERMINAL_PROSEGUR, PAYTERMINAL_CASHLOGY } from '@app/modules/model/payment-terminal';

import { languageService, languageSupport } from '@app/modules/common/language';
import { alertService } from '@app/modules/common/alert';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-payment-terminal-devices',
    templateUrl: 'ui-payment-terminal.html',
    styleUrls: [
      'ui-payment-terminal.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiPaymentTerminalDevices extends languageSupport implements OnInit, OnChanges, OnDestroy {
    @Input('service') _service: PlaceService = null;
    @Input('editdevice') _editdevice: any = null;

    /*****************************/
    /* COMPONENT DATA SHARING    */
    /*****************************/

    private get EditDevice(){
        return this._editdevice;
    }

    DiscardChanges(){
        this._RefreshDevices();
        this.IsDirty = false;
        this.LoadTerminalForm();
    }

    @Output('OnUpdate') _onupdate = new EventEmitter <any> ();
    private set EditDevice(value){
        this._editdevice = value;

        this._onupdate.next();
        this.change.markForCheck();
    }

    @Output('OnChange') _onchange = new EventEmitter <any> ();
    OnViewChanged(){
        this.change.markForCheck();
        this._onchange.emit();
    }

    get IsValid(){
        return true;
    }

    private _isdirty = false;
    get IsDirty(){
        if (!this.IsValid){
            return false;
        }

        return this._isdirty;
    }

    set IsDirty(value){
        this._isdirty = value;
        if (value == false){
            this.TerminalForm.markAsPristine();
        }

        this.OnViewChanged();
    }

    private _terminals: Array<PaymentTerminal> = [];
    get PlaceTerminals(){
        return this._terminals || [];
    }

    /*****************************/
    /* COMPONENT DATA ACCESS     */
    /*****************************/

    private _terminalForm = null;
    get TerminalForm(){
        if (this._terminalForm == null){
            this.LoadTerminalForm();
        }
        return this._terminalForm;
    }

    /* Modelos de datafono ofrecidos en el desplegable. El driver decide como
       se cobra: PAYTEF por IP, PROSEGUR via MDW por identificador POS y
       CASHLOGY por ConnectorPlus. */
    get TerminalModels(){
        let _options = [ PAYTERMINAL_PAYTEF, PAYTERMINAL_PROSEGUR, PAYTERMINAL_CASHLOGY ];

        let _values = [];
        for(let _option of _options){
            _values.push({
                value: _option, text: _option
            })
        }

        return _values;
    }

    /* ui-picker guarda en el form control la OPCION completa ({text, value}),
       no el codigo pelado. Estos dos helpers son el puente con el resto del
       componente, que trabaja con el codigo string (PAYTEF/PROSEGUR/CASHLOGY):
        - _modelCode: extrae el codigo del valor del control. Tolera un string
          suelto (terminal ya guardado) y null.
        - _modelOption: envuelve un codigo en la opcion que el picker necesita
          para MOSTRAR el modelo actual al cargar el formulario.
       Sin esto, al elegir en el picker el control pasaba a ser un objeto:
       EditDevice.model quedaba en `[object Object]` y las comparaciones
       `== PROSEGUR` (direccionamiento POS, validacion de host) fallaban. */
    private _modelCode(value: any): string {
        return (value && typeof value === 'object') ? value.value : value;
    }
    private _modelOption(code: any){
        // Normaliza por si llega un objeto (modelo de un guardado previo con el
        // bug) o un string: el picker siempre recibe {text, value} limpio.
        const _code = this._modelCode(code);
        return _code ? { text: _code, value: _code } : null;
    }

    /* PROSEGUR direcciona por el alias que el MDW da al dispositivo
       (PaymentTerminal, PaymentTerminal2...), no por IP: cambia la etiqueta y
       la validacion del campo 'host'. */
    get IsPosAddressed(){
        return (this._modelCode(this.TerminalForm.get('model').value) == PAYTERMINAL_PROSEGUR);
    }

    get HostTitleKey(){
        return this.IsPosAddressed ? '@place_input_terminal_devid' : '@place_input_terminal_host';
    }

    get HostPlaceholderKey(){
        return this.IsPosAddressed ? '@place_input_terminal_devid_ph' : '@place_input_terminal_host_ph';
    }

    /* La direccion solo es obligatoria en PAYTEF, que necesita la IP del
       datafono. En PROSEGUR el MDW resuelve el dispositivo por su cuenta
       (confirmado por Prosegur: con varios datafonos los diferencia el
       middleware), asi que el alias es opcional: si se deja vacio no se manda
       `deviceId` y el MDW usa su PaymentTerminal por defecto. Se conserva el
       campo para poder fijar un datafono concreto si algun dia hace falta. */
    private _hostRequired(){
        return (control: FormControl) => {
            if (this._terminalForm && (this._modelCode(this._terminalForm.get('model').value) == PAYTERMINAL_PROSEGUR)){
                return null;    // opcional: lo resuelve el MDW
            }

            let _value = (control.value || '').toString().trim();
            return _value ? null : { required: true };
        };
    }

    /* PAYTEF direcciona por IP/hostname; PROSEGUR por el alias que el MDW
       asigna en la instalacion, que puede ser cualquier cadena: ahi solo
       exigimos que no este vacio. */
    private _hostValidator(hostRe: RegExp){
        return (control: FormControl) => {
            let _value = (control.value || '').toString().trim();
            if (!_value){
                // El "vacio" lo decide _hostRequired (obligatorio salvo en
                // PROSEGUR, donde el host es opcional porque lo resuelve el MDW);
                // aqui solo validamos el formato de un host NO vacio.
                return null;
            }

            if (this._terminalForm && (this._modelCode(this._terminalForm.get('model').value) == PAYTERMINAL_PROSEGUR)){
                return null;    // identificador libre
            }

            return hostRe.test(_value) ? null : { pattern: true };
        };
    }

    /* patchValue dispara valueChanges igual que una edicion del usuario. Sin
       esta bandera, cargar el formulario de un datafono existente le borraria
       la direccion guardada. */
    private _loadingform = false;

    private LoadTerminalForm(){
        this._loadingform = true;
        try {
            this._LoadTerminalFormValues();
        }
        finally {
            this._loadingform = false;
        }
    }

    private _LoadTerminalFormValues(){
        let _name = null;
        let _model = PAYTERMINAL_PAYTEF;
        let _host = null;
        let _onpaycash = false;
        let _onpaycard = false;
        let _nocash = false;

        if (this.EditDevice){
            _name = this.EditDevice.name;
            _model = this.EditDevice.model || PAYTERMINAL_PAYTEF;
            _host = this.EditDevice.host;
            _onpaycash = !!this.EditDevice.onpaycash;
            _onpaycard = !!this.EditDevice.onpaycard;
            _nocash = !!this.EditDevice.nocash;
        }

        if (this._terminalForm == null){
            // host: IPv4 (1-3 digits per octet) OR a DNS hostname
            // (letters, digits, dot, hyphen). Loose on purpose — the
            // device could be reachable by hostname in some setups,
            // and the daemon will fail clearly if the value is wrong;
            // we just want to stop obviously-garbage entries from
            // being saved silently.
            let _hostRe = /^([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?)*)$/;

            this._terminalForm = new FormGroup({
                name: new FormControl(_name, [
                    Validators.required
                ]),
                // El control guarda la OPCION del picker ({text, value}), no el
                // codigo pelado — es lo que ui-picker necesita para mostrar el
                // modelo actual. El codigo se extrae con _modelCode al leerlo.
                model: new FormControl(this._modelOption(_model), [
                    Validators.required
                ]),
                host: new FormControl(_host, [
                    this._hostRequired(),
                    this._hostValidator(_hostRe)
                ]),
                onpaycash: new FormControl(_onpaycash, [
                ]),
                onpaycard: new FormControl(_onpaycard, [
                ]),
                nocash: new FormControl(_nocash, [
                ])
            })

            // nocash gates onpaycash: si se bloquea el pago en efectivo,
            // imprimir el ticket al pagar en efectivo no aplica — lo
            // forzamos a false y disabilitamos el control para que la UI
            // muestre la fila atenuada y el usuario no pueda interactuar.
            this._terminalForm.get('nocash').valueChanges.subscribe(_nocash => {
                this._syncNocashGate(!!_nocash);
            });

            // El modelo cambia el SIGNIFICADO de 'host': en PAYTEF es la IP del
            // datafono y en PROSEGUR el alias con el que el MDW lo identifica.
            // Arrastrar el valor anterior mandaria una IP como deviceId (y el
            // MDW respondria DEVICE_UNKNOWN), asi que se limpia al cambiar y se
            // revalida con las reglas del modelo nuevo.
            this._terminalForm.get('model').valueChanges.subscribe(() => {
                if (this._loadingform){
                    return;     // carga del formulario: no es un cambio del usuario
                }

                let _host = this._terminalForm.get('host');
                if (_host.value){
                    _host.setValue(null);
                }
                _host.updateValueAndValidity();
                this.change.markForCheck();
            });
        }
        else {
            this._terminalForm.patchValue({
                name: _name,
                model: this._modelOption(_model),
                host: _host,
                onpaycash: _onpaycash,
                onpaycard: _onpaycard,
                nocash: _nocash
            });
        }

        // Aplicar el gate tanto en creacion como tras patchValue (carga de
        // un terminal existente que ya tenia nocash=true).
        this._syncNocashGate(_nocash);

        // Revalidar 'host' con el form YA asignado: al construir el FormGroup,
        // los validadores corren mientras `this._terminalForm` sigue siendo null
        // (aun no ha retornado el `new FormGroup`), asi que _hostRequired no ve
        // que el modelo es PROSEGUR y marca `required` un host vacio que en
        // PROSEGUR es opcional. Sin esto, cargar un PROSEGUR sin host dejaba el
        // form invalido y no se podia aceptar. Ahora los validadores ven el form
        // y el modelo reales.
        this._terminalForm.get('host').updateValueAndValidity({ emitEvent: false });

        this._terminalForm.markAsPristine();
        setTimeout(() => {
            this._terminalForm.markAsPristine();
        }, 0);

        this.change.markForCheck();
    }

    private _syncNocashGate(nocash: boolean){
        const _onpaycashCtrl = this._terminalForm.get('onpaycash');
        if (!_onpaycashCtrl){
            return;
        }
        if (nocash){
            if (_onpaycashCtrl.value){
                _onpaycashCtrl.setValue(false, { emitEvent: false });
            }
            _onpaycashCtrl.disable({ emitEvent: false });
        }
        else {
            _onpaycashCtrl.enable({ emitEvent: false });
        }
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _service_subscription = null;

    private _RefreshDevices(){
        this._terminals = this._PlaceTerminals;
        this.IsDirty = false;

        this.OnViewChanged();
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private get _PlaceTerminals(){
        let _terminals = [];
        if (this._service){
            for(let _terminal of this._service.payment_terminals)
            if (_terminal.IsValid){
                _terminals.push(_terminal);
            }
        }

        return _terminals;
    }

    ngOnInit(){
        this._RefreshDevices();
    }

    ngOnChanges(changes: SimpleChanges) : void {
        if ('_service' in changes){
            this._RefreshDevices();

            if (this._service_subscription){
                this._service_subscription.unsubscribe();
                this._service_subscription = null;
            }

            this._service_subscription = this._service.OnRefresh.subscribe(
            data => {
                if (!this.IsDirty){
                    this._RefreshDevices();
                }
                this.change.markForCheck();
            });
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    private _expand = false;
    get ExpandTerminals(){
        return this._expand;
    }

    @ViewChild('devices', { static: false }) _devices: ElementRef;
    OnExpandTerminals(){
        this._expand = !this._expand;

        if (this._devices){
            let _devices = this._devices.nativeElement;
            if (this._expand){
                _devices.style.height = _devices.scrollHeight + 'px';
                setTimeout(() => {
                    _devices.style.height = 'auto';
                }, 200);
            }
            else {
                _devices.style.height = _devices.scrollHeight + 'px';
                _devices.offsetHeight;
                _devices.style.height = '0px';
            }
        }

        this.change.markForCheck();
    }

    OnAddDevice(){
        this._isdirty = true;

        let _device = new PaymentTerminal(null, this.data);
        if (_device){
            _device.rasppi = this._service;
            _device.status = 'AC'
            _device.model = PAYTERMINAL_PAYTEF;
        }

        this.EditDevice = _device;
        this.LoadTerminalForm();
        this.EditDevice['operation'] = 'add';

        let _idx = this._terminals.indexOf(_device);
        if (_idx == -1){
            this._terminals.unshift(this.EditDevice);
        }

        this._expand = true;
        this.change.markForCheck();
    }

    OnSetDevice(device){
        this.EditDevice = device;
        this.LoadTerminalForm();
        this.EditDevice['operation'] = 'set';

        this.change.markForCheck();
    }

    private _DelDevice(_device){
        _device.status = 'DE';

        let _idx = this._terminals.indexOf(_device);
        if (_idx != -1){
            this._terminals.splice(_idx, 1);
        }

        this.EditDevice = null;
        this._isdirty = true;       // mirrors OnAddDevice: any list change is dirty
        this.change.markForCheck();
    }

    async OnDelDevice(device){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_terminal_delete_confirm'),
            message: this.lang.tr('@place_terminal_delete_message', [ device.name || this.lang.tr('@place_device_terminal_noname') ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._DelDevice(device);
            }
        });

        await alert.present();
    }

    /****************************/
    /* APPLY CHANGES            */
    /****************************/

    private _ApplyTerminalForm(){
        this.EditDevice.name = this.TerminalForm.get('name').value;
        // _modelCode: el control lleva la opcion {text, value} del picker; se
        // guarda solo el codigo (PAYTEF/PROSEGUR/CASHLOGY) para no persistir un
        // objeto (que aparecia como "[object Object]" y rompia el routing).
        this.EditDevice.model = this._modelCode(this.TerminalForm.get('model').value);
        this.EditDevice.host = this.TerminalForm.get('host').value;
        this.EditDevice.onpaycash = !!this.TerminalForm.get('onpaycash').value;
        this.EditDevice.onpaycard = !!this.TerminalForm.get('onpaycard').value;
        this.EditDevice.nocash = !!this.TerminalForm.get('nocash').value;

        if (this.EditDevice.name){
            this.EditDevice.name = this.EditDevice.name.trim();
        }

        if (this.EditDevice.host){
            this.EditDevice.host = this.EditDevice.host.trim();
        }
    }

    /****************************/
    /* COMMIT ACTIONS           */
    /****************************/

    OnAcceptDevice(){
        this._ApplyTerminalForm();
        this.TerminalForm.markAsPristine();

        this.EditDevice = null;
        this.IsDirty = true;
    }

    OnCancelDevice(){
        this.TerminalForm.markAsPristine();

        if (this.EditDevice['operation'] == 'add'){
            this._DelDevice(this.EditDevice);
        }

        this.EditDevice = null;
    }
}
